import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import {
  FlaskConical, History, Play, AlertCircle, Trash2, CheckCircle2,
  GitCompare, X, ShieldCheck, ShieldAlert, Grid3X3, AlertTriangle,
} from 'lucide-react';
import {
  API_URL, authHeaders, formatUSD, formatTs,
} from './ops_helpers';

// ============== PRESETS ==============
// Default reflejo de tu config LIVE en el bot LBFP (FTMO $10k Challenge)
const DEFAULTS_FTMO = {
  symbol: 'XAUUSD',
  initial_balance: 10000,
  risk_pct: 0.008,
  sl_pips: 8,
  tp_pips: 24,
  pip_size: 0.10,
  pip_value_per_lot: 10,
  contract_size: 100,
  lot_cap: 20,
  commission_per_lot: 4,
  spread_pips: 5,
  // Realismo de ejecucion (FIX auditoria jun-2026)
  slippage_pips: 3,
  model_gap_through: false,
  include_entry_bar: true,
  // SL dinamico por volatilidad
  sl_mode: 'fixed',
  atr_period: 14,
  atr_mult: 1.0,
  rr: 0.0,
  // === Motor de backtest ===
  engine_mode: 'h1',            // 'h1' (rapido, H1) | 'm1' (alta fidelidad, OCO real)
  // Alta fidelidad M1 (ventana OCO + filtros de mejora)
  entry_start_utc: 7,
  entry_end_utc: 12,
  spread_buffer_pips: 5,
  max_lot: 50,
  exec_extra_pips: 1,
  exit_extra_pips: 1,
  entry_buffer_pips: 0,
  min_range_pips: 0,
  max_range_pips: 1000000000,
  htf_filter: false,
  htf_tf: 'd1',
  htf_ema_period: 20,
  be_at_r: 0,
  trail_start_r: 0,
  trail_give_r: 0.5,
  partial_tp_r: 0,
  partial_close_pct: 0.5,
  asian_start_utc: 0,
  asian_end_utc: 7,
  london_start_utc: 8,
  london_end_utc: 8,
  // NY breakout (Opcion B: bar 13:00 vs rango London 08:00-12:59)
  enable_ny_breakout: false,
  ny_range_start_utc: 8,
  ny_range_end_utc: 13,
  ny_hour_utc: 13,
  max_trades_per_day: 2,
  stop_on_win: true,
  trail1_pct: 0.019,
  trail2_pct: 0.022,
  trail2_lock_pct: 0.01,
  period_days: null,
  note: '',
  // Monte Carlo
  monte_carlo_n_sims: 0,
  monte_carlo_mode: 'shuffle',
  monte_carlo_seed: 42,
  monte_carlo_daily_dd_limit_pct: 5.0,
  monte_carlo_total_dd_limit_pct: 10.0,
  monte_carlo_target_pct: 10.0,
};

const PRESET_LIVE = {
  ...DEFAULTS_FTMO,
  note: 'LBFP $10k Live (config produccion)',
};

const PRESET_LONDON_NY = {
  ...DEFAULTS_FTMO,
  enable_ny_breakout: true,
  max_trades_per_day: 3,
  note: 'London 08:00 + NY 13:00 (Opcion B, rango London)',
};

const PRESET_AGGRESSIVE = {
  ...DEFAULTS_FTMO,
  risk_pct: 0.012,
  sl_pips: 10,
  tp_pips: 30,
  spread_pips: 5,
  note: 'LBFP agresivo 1.2% risk / 10 SL / 30 TP',
};

const PRESET_LONDON_FULL = {
  ...DEFAULTS_FTMO,
  london_end_utc: 9,
  max_trades_per_day: 2,
  note: 'LBFP ventana 08:00-09:00',
};

const PRESET_SWEEP_SPREAD3 = {
  ...DEFAULTS_FTMO,
  spread_pips: 3,
  note: 'Sweep spread 3 pips (broker A)',
};
const PRESET_SWEEP_SPREAD7 = {
  ...DEFAULTS_FTMO,
  spread_pips: 7,
  note: 'Sweep spread 7 pips (broker C / news)',
};

const PRESET_3Y = {
  ...DEFAULTS_FTMO,
  period_days: 1095,
  monte_carlo_n_sims: 1000,
  monte_carlo_mode: 'shuffle',
  note: '3 anios + Monte Carlo 1000 sims (shuffle)',
};

const PRESET_MC_BOOTSTRAP = {
  ...DEFAULTS_FTMO,
  monte_carlo_n_sims: 2000,
  monte_carlo_mode: 'bootstrap',
  note: 'Monte Carlo bootstrap 2000 sims (proyeccion forward)',
};

const PRESET_ATR_C3 = {
  ...DEFAULTS_FTMO,
  sl_mode: 'atr',
  atr_mult: 1.0,
  rr: 2.0,
  sl_pips: 8,        // piso minimo de SL (el ATR manda si es mayor)
  risk_pct: 0.005,
  note: 'ATR c3 (estrategia VIVA): SL=ATR(14)x1.0, TP=2xSL, riesgo 0.5%',
};

const PRESET_M1_C3B = {
  ...DEFAULTS_FTMO,
  engine_mode: 'm1',
  sl_mode: 'atr',
  atr_mult: 1.0,
  rr: 2.5,
  sl_pips: 8,
  risk_pct: 0.005,
  entry_start_utc: 7,
  entry_end_utc: 12,
  max_trades_per_day: 1,
  note: 'M1 ALTA FIDELIDAD — C3b (ATRx1.0 RR2.5, ventana OCO 07-12, fiel al bot vivo)',
};

const tooltipStyle = {
  backgroundColor: '#0a0a0b',
  border: '1px solid #3f3f46',
  borderRadius: '8px',
  fontSize: '12px',
};

const NumberField = ({ label, value, onChange, step, hint, testid }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-zinc-400">{label}</label>
    <Input
      type="number"
      step={step || 'any'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
      className="h-8 text-sm bg-zinc-900 border-zinc-700"
      data-testid={testid || `ftmo-bt-input-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    />
    {hint && <span className="text-[10px] text-zinc-500">{hint}</span>}
  </div>
);

const Toggle = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
    <input
      type="checkbox"
      checked={!!value}
      onChange={(e) => onChange(e.target.checked)}
      className="accent-amber-500"
      data-testid={testid || `ftmo-bt-toggle-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    />
    {label}
  </label>
);

// ============== MONTE CARLO PANEL ==============
const StatRow = ({ label, p5, p25, p50, p75, p95, suffix = '', highlight = false }) => (
  <TableRow className={highlight ? 'bg-violet-950/30' : ''}>
    <TableCell className="text-xs font-medium">{label}</TableCell>
    <TableCell className="text-right text-xs font-mono text-red-300">{p5}{suffix}</TableCell>
    <TableCell className="text-right text-xs font-mono text-amber-300">{p25}{suffix}</TableCell>
    <TableCell className="text-right text-xs font-mono text-zinc-100">{p50}{suffix}</TableCell>
    <TableCell className="text-right text-xs font-mono text-emerald-300">{p75}{suffix}</TableCell>
    <TableCell className="text-right text-xs font-mono text-emerald-400">{p95}{suffix}</TableCell>
  </TableRow>
);

const MonteCarloPanel = ({ mc }) => {
  // Convertir curvas a data para recharts. X = trade index, Y = balance
  const chartData = useMemo(() => {
    if (!mc?.worst_curve || !mc?.median_curve || !mc?.best_curve) return [];
    const w = mc.worst_curve.curve;
    const m = mc.median_curve.curve;
    const b = mc.best_curve.curve;
    const n = Math.max(w.length, m.length, b.length);
    const data = [];
    for (let i = 0; i < n; i += 1) {
      data.push({
        idx: i,
        worst: w[i] ?? null,
        median: m[i] ?? null,
        best: b[i] ?? null,
      });
    }
    return data;
  }, [mc]);

  if (!mc || mc.skipped) return null;

  const passRate = mc.compliance_pass_rate;
  const targetRate = mc.target_pass_rate;
  const passColor = passRate >= 90 ? 'text-emerald-400' : passRate >= 70 ? 'text-amber-400' : 'text-red-400';
  const targetColor = targetRate >= 70 ? 'text-emerald-400' : targetRate >= 40 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="pt-4 mt-2 border-t-2 border-violet-900/60" data-testid="ftmo-mc-results">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-semibold text-violet-300">Monte Carlo</h3>
          <Badge variant="outline" className="border-violet-700 text-violet-300 text-[10px]">
            {mc.n_sims} sims · {mc.mode}
          </Badge>
        </div>
        <span className="text-[10px] text-zinc-500">
          {mc.trades_per_sim} trades/sim · seed {mc.seed}
        </span>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-zinc-900 border border-violet-900/40 rounded p-3" data-testid="ftmo-mc-compliance">
          <p className="text-[10px] text-zinc-400 uppercase">FTMO Compliance Pass Rate</p>
          <p className={`text-2xl font-bold ${passColor} font-mono`}>{passRate}%</p>
          <p className="text-[10px] text-zinc-500">
            Limites: daily {mc.limits.daily_dd_limit_pct}% · total {mc.limits.total_dd_limit_pct}%
          </p>
        </div>
        <div className="bg-zinc-900 border border-violet-900/40 rounded p-3" data-testid="ftmo-mc-target">
          <p className="text-[10px] text-zinc-400 uppercase">Target Hit Rate ({mc.limits.target_pct}%+)</p>
          <p className={`text-2xl font-bold ${targetColor} font-mono`}>{targetRate}%</p>
          <p className="text-[10px] text-zinc-500">% sims cumplen Compliance Y alcanzan target</p>
        </div>
      </div>

      {/* Tabla de percentiles */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-2 mb-3 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="text-[10px] uppercase">
              <TableHead>Metrica</TableHead>
              <TableHead className="text-right">P5 (peor)</TableHead>
              <TableHead className="text-right">P25</TableHead>
              <TableHead className="text-right">P50 (mediana)</TableHead>
              <TableHead className="text-right">P75</TableHead>
              <TableHead className="text-right">P95 (mejor)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <StatRow label="Balance final USD" highlight
              p5={formatUSD(mc.final_balance.p5)}
              p25={formatUSD(mc.final_balance.p25)}
              p50={formatUSD(mc.final_balance.p50)}
              p75={formatUSD(mc.final_balance.p75)}
              p95={formatUSD(mc.final_balance.p95)} />
            <StatRow label="Max Drawdown %"
              p5={mc.max_dd_pct.p5}
              p25={mc.max_dd_pct.p25}
              p50={mc.max_dd_pct.p50}
              p75={mc.max_dd_pct.p75}
              p95={mc.max_dd_pct.p95}
              suffix="%" />
            <StatRow label="Max Daily DD %"
              p5={mc.max_daily_dd_pct.p5}
              p25={mc.max_daily_dd_pct.p25}
              p50={mc.max_daily_dd_pct.p50}
              p75={mc.max_daily_dd_pct.p75}
              p95={mc.max_daily_dd_pct.p95}
              suffix="%" />
          </TableBody>
        </Table>
        <p className="text-[10px] text-zinc-500 mt-1 px-1">
          Mean balance: {formatUSD(mc.final_balance.mean)} · Std: {formatUSD(mc.final_balance.std)} ·
          Worst DD: {mc.max_dd_pct.max}% · Worst Daily DD: {mc.max_daily_dd_pct.max}%
        </p>
      </div>

      {/* Curvas peor/mediana/mejor */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-2">
        <p className="text-[10px] text-zinc-400 uppercase mb-1">Curvas de equity — peor / mediana / mejor</p>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="idx" tick={{ fontSize: 10, fill: '#71717a' }}
              label={{ value: 'trade #', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#71717a' }} />
            <YAxis tick={{ fontSize: 10, fill: '#71717a' }}
              tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
            <Tooltip contentStyle={tooltipStyle}
              formatter={(v) => formatUSD(v)}
              labelFormatter={(l) => `trade #${l}`} />
            <ReferenceLine y={mc.final_balance.p50}
              stroke="#a78bfa" strokeDasharray="2 4" strokeOpacity={0.4} />
            <Line type="monotone" dataKey="best" stroke="#34d399" strokeWidth={1.5} dot={false}
              name={`Mejor ($${(mc.best_curve.final_balance / 1000).toFixed(2)}k)`} />
            <Line type="monotone" dataKey="median" stroke="#a78bfa" strokeWidth={2} dot={false}
              name={`Mediana ($${(mc.median_curve.final_balance / 1000).toFixed(2)}k)`} />
            <Line type="monotone" dataKey="worst" stroke="#f87171" strokeWidth={1.5} dot={false}
              name={`Peor ($${(mc.worst_curve.final_balance / 1000).toFixed(2)}k)`} />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-zinc-500 mt-1 px-1">
          Peor DD: {mc.worst_curve.max_dd_pct}% · Mediana DD: {mc.median_curve.max_dd_pct}% · Mejor DD: {mc.best_curve.max_dd_pct}%
        </p>
      </div>
    </div>
  );
};

// ============== AUTO-OPTIMIZE PANEL ==============
const OptimizeFTMOPanel = ({ loading, result, onApply, targetCompliance }) => {
  const chartData = useMemo(() => {
    if (!result?.sweep) return [];
    return result.sweep
      .filter((r) => !r.skipped)
      .map((r) => ({
        risk_pct: +(r.risk_pct * 100).toFixed(2),
        return_pct: r.return_pct,
        mc_compliance_pass_rate: r.mc_compliance_pass_rate,
        mc_max_dd_p95: r.mc_max_dd_p95,
        real_dd: r.max_dd_pct_real,
      }));
  }, [result]);

  if (loading) {
    return (
      <Card className="lg:col-span-3 bg-zinc-950 border-violet-900/60" data-testid="ftmo-optimize-card-loading">
        <CardContent className="py-10 text-center">
          <span className="animate-spin inline-block mr-2">⏳</span>
          <span className="text-violet-300 text-sm">
            Auto-optimizando risk_pct... esto puede tardar 30-90s en datasets de 3 anios.
          </span>
        </CardContent>
      </Card>
    );
  }

  if (!result) return null;

  const opt = result.optimal;
  const optColor = opt && (opt.mc_compliance_pass_rate || 0) >= targetCompliance
    ? 'border-emerald-700 bg-emerald-950/30 text-emerald-300'
    : 'border-amber-700 bg-amber-950/30 text-amber-300';

  return (
    <Card className="lg:col-span-3 bg-zinc-950 border-violet-900/60" data-testid="ftmo-optimize-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-violet-400" />
            Auto-optimizador FTMO — Sweep risk_pct + Monte Carlo
          </span>
          <Badge variant="outline" className="border-violet-700 text-violet-300 text-xs">
            {result.n_iterations} iters · {result.duration_sec}s · {result.monte_carlo_n_sims} MC/iter · target {result.target_compliance_pct}%
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Optimal banner */}
        {opt ? (
          <div className={`rounded p-3 border mb-3 flex items-center justify-between ${optColor}`} data-testid="ftmo-optimal-banner">
            <div>
              <p className="text-[10px] uppercase opacity-70">Mejor configuracion encontrada</p>
              <p className="text-lg font-bold font-mono">
                risk_pct = {(opt.risk_pct * 100).toFixed(2)}%
              </p>
              <p className="text-xs opacity-80">
                Return {opt.return_pct}% · Compliance {opt.mc_compliance_pass_rate}% · MaxDD P95 {opt.mc_max_dd_p95}% · {opt.total_trades} trades · PF {opt.profit_factor ?? '—'}
              </p>
              <p className="text-[10px] italic opacity-60 mt-1">{result.optimal_reason}</p>
            </div>
            <Button
              onClick={onApply}
              className="bg-violet-700 hover:bg-violet-600 text-white"
              data-testid="ftmo-apply-optimal-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Aplicar al form
            </Button>
          </div>
        ) : (
          <div className="rounded p-3 border border-red-800 bg-red-950/30 text-red-300 mb-3 text-sm">
            No se encontro configuracion optima. {result.optimal_reason}
          </div>
        )}

        {/* Sweep table + chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded p-2 overflow-x-auto">
            <p className="text-[10px] text-zinc-400 uppercase mb-1">Tabla del sweep</p>
            <Table>
              <TableHeader>
                <TableRow className="text-[10px] uppercase">
                  <TableHead>Risk%</TableHead>
                  <TableHead className="text-right">Return%</TableHead>
                  <TableHead className="text-right">Real DD%</TableHead>
                  <TableHead className="text-right">MC DD P95</TableHead>
                  <TableHead className="text-right">MC Compl%</TableHead>
                  <TableHead className="text-right">Trades</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.sweep.map((r) => {
                  const isOptimal = opt && Math.abs((r.risk_pct - opt.risk_pct)) < 1e-6;
                  const passes = (r.mc_compliance_pass_rate || 0) >= targetCompliance;
                  return (
                    <TableRow
                      key={r.risk_pct}
                      className={isOptimal ? 'bg-violet-950/40' : ''}
                      data-testid={`ftmo-opt-row-${(r.risk_pct * 100).toFixed(2)}`}
                    >
                      <TableCell className="text-xs font-mono font-semibold">
                        {(r.risk_pct * 100).toFixed(2)}%
                        {isOptimal && <span className="text-violet-400 ml-1">★</span>}
                      </TableCell>
                      {r.skipped ? (
                        <TableCell colSpan={5} className="text-xs text-red-400 italic">
                          error: {r.error}
                        </TableCell>
                      ) : (
                        <>
                          <TableCell className={`text-right text-xs font-mono ${(r.return_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {r.return_pct?.toFixed(2)}%
                          </TableCell>
                          <TableCell className="text-right text-xs text-red-300 font-mono">{r.max_dd_pct_real?.toFixed(2)}%</TableCell>
                          <TableCell className="text-right text-xs text-amber-300 font-mono">{r.mc_max_dd_p95}%</TableCell>
                          <TableCell className={`text-right text-xs font-mono font-semibold ${passes ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {r.mc_compliance_pass_rate}%
                          </TableCell>
                          <TableCell className="text-right text-xs">{r.total_trades}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded p-2">
            <p className="text-[10px] text-zinc-400 uppercase mb-1">Curva — Return vs Compliance</p>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis
                  dataKey="risk_pct"
                  tick={{ fontSize: 10, fill: '#71717a' }}
                  label={{ value: 'risk_pct (%)', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#71717a' }}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#71717a' }}
                  tickFormatter={(v) => `${v}%`}
                  label={{ value: 'return / DD %', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#71717a' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#a78bfa' }}
                  tickFormatter={(v) => `${v}%`} domain={[0, 100]}
                  label={{ value: 'compliance %', angle: 90, position: 'insideRight', fontSize: 10, fill: '#a78bfa' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <ReferenceLine yAxisId="right" y={targetCompliance} stroke="#a78bfa" strokeDasharray="2 4" strokeOpacity={0.5} />
                <Line yAxisId="left" type="monotone" dataKey="return_pct" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} name="Return %" />
                <Line yAxisId="left" type="monotone" dataKey="mc_max_dd_p95" stroke="#f59e0b" strokeWidth={1.5} dot={{ r: 2 }} name="MaxDD P95 %" />
                <Line yAxisId="left" type="monotone" dataKey="real_dd" stroke="#ef4444" strokeWidth={1} strokeDasharray="3 3" dot={false} name="Real DD %" />
                <Line yAxisId="right" type="monotone" dataKey="mc_compliance_pass_rate" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} name="Compliance %" />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-zinc-500 mt-1 px-1">
              Verde = return real · Naranja = MC MaxDD P95 · Rojo punteado = DD real backtest · Violeta = MC Compliance pass rate (eje derecho).
              Linea horizontal violeta = target {targetCompliance}%.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== AUTO-OPTIMIZE 2D PANEL ==============
const Optimize2DPanel = ({ loading, result, onApply, targetCompliance, metric, onMetricChange }) => {
  // Construir grid SL (filas) x TP (columnas) → cellMap[sl][tp] = cell
  const grid = useMemo(() => {
    if (!result?.cells) return { rows: [], cols: [], map: {} };
    const sls = result.sl_values || [];
    const tps = result.tp_values || [];
    const map = {};
    for (const c of result.cells) {
      if (!map[c.sl_pips]) map[c.sl_pips] = {};
      map[c.sl_pips][c.tp_pips] = c;
    }
    return { rows: sls, cols: tps, map };
  }, [result]);

  // Calcular color de fondo segun metrica (rojo → verde)
  const getCellColor = (cell) => {
    if (!cell || cell.skipped) return { bg: '#18181b', fg: '#52525b' };
    const v = metric === 'compliance'
      ? (cell.mc_compliance_pass_rate || 0)
      : (cell.return_pct || 0);
    if (metric === 'compliance') {
      // 0-50 rojo, 50-80 amarillo, 80-95 verde claro, 95-100 verde fuerte
      if (v >= 95) return { bg: 'rgba(16, 185, 129, 0.55)', fg: '#a7f3d0' };
      if (v >= 85) return { bg: 'rgba(16, 185, 129, 0.30)', fg: '#86efac' };
      if (v >= 70) return { bg: 'rgba(245, 158, 11, 0.30)', fg: '#fcd34d' };
      if (v >= 50) return { bg: 'rgba(249, 115, 22, 0.30)', fg: '#fdba74' };
      return { bg: 'rgba(239, 68, 68, 0.30)', fg: '#fca5a5' };
    }
    // Return: <0 rojo, 0-25 amarillo, 25-50 verde claro, 50+ verde fuerte
    if (v >= 50) return { bg: 'rgba(16, 185, 129, 0.55)', fg: '#a7f3d0' };
    if (v >= 25) return { bg: 'rgba(16, 185, 129, 0.30)', fg: '#86efac' };
    if (v >= 0) return { bg: 'rgba(245, 158, 11, 0.30)', fg: '#fcd34d' };
    return { bg: 'rgba(239, 68, 68, 0.35)', fg: '#fca5a5' };
  };

  if (loading) {
    return (
      <Card className="lg:col-span-3 bg-zinc-950 border-cyan-900/60" data-testid="ftmo-opt2d-card-loading">
        <CardContent className="py-10 text-center">
          <span className="animate-spin inline-block mr-2">⏳</span>
          <span className="text-cyan-300 text-sm">
            Generando heatmap 2D... cada celda corre 1 backtest 3a + 300 MC sims.
          </span>
        </CardContent>
      </Card>
    );
  }

  if (!result) return null;

  const opt = result.optimal;
  const optColor = opt && (opt.mc_compliance_pass_rate || 0) >= targetCompliance
    ? 'border-emerald-700 bg-emerald-950/30 text-emerald-300'
    : 'border-amber-700 bg-amber-950/30 text-amber-300';

  return (
    <Card className="lg:col-span-3 bg-zinc-950 border-cyan-900/60" data-testid="ftmo-opt2d-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Grid3X3 className="w-4 h-4 text-cyan-400" />
            Optimizer 2D — Heatmap SL × TP (risk fijo)
          </span>
          <Badge variant="outline" className="border-cyan-700 text-cyan-300 text-xs">
            {result.n_cells} celdas ({result.n_valid} validas) · {result.duration_sec}s · risk {(result.risk_pct_used * 100).toFixed(2)}% · target {result.target_compliance_pct}%
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Optimal banner */}
        {opt ? (
          <div className={`rounded p-3 border mb-3 flex items-center justify-between ${optColor}`} data-testid="ftmo-opt2d-banner">
            <div>
              <p className="text-[10px] uppercase opacity-70">Mejor configuracion SL/TP encontrada</p>
              <p className="text-lg font-bold font-mono">
                SL = {opt.sl_pips} pips · TP = {opt.tp_pips} pips · R:R 1:{opt.rr_ratio}
              </p>
              <p className="text-xs opacity-80">
                Return {opt.return_pct}% · Compliance {opt.mc_compliance_pass_rate}% · MaxDD P95 {opt.mc_max_dd_p95}% · {opt.total_trades} trades · WR {opt.win_rate}% · PF {opt.profit_factor ?? '—'}
              </p>
              <p className="text-[10px] italic opacity-60 mt-1">{result.optimal_reason}</p>
            </div>
            <Button
              onClick={onApply}
              className="bg-cyan-700 hover:bg-cyan-600 text-white"
              data-testid="ftmo-apply-optimal2d-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Aplicar al form
            </Button>
          </div>
        ) : (
          <div className="rounded p-3 border border-red-800 bg-red-950/30 text-red-300 mb-3 text-sm">
            No se encontro configuracion optima. {result.optimal_reason}
          </div>
        )}

        {/* Toggle metric */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-zinc-400">Color por:</span>
          <Button
            size="sm" variant="outline"
            onClick={() => onMetricChange('compliance')}
            className={`h-6 px-2 text-[11px] ${metric === 'compliance' ? 'bg-cyan-700/40 border-cyan-600 text-cyan-200' : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'}`}
            data-testid="ftmo-opt2d-metric-compliance"
          >
            MC Compliance %
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => onMetricChange('return')}
            className={`h-6 px-2 text-[11px] ${metric === 'return' ? 'bg-cyan-700/40 border-cyan-600 text-cyan-200' : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'}`}
            data-testid="ftmo-opt2d-metric-return"
          >
            Return %
          </Button>
        </div>

        {/* Heatmap */}
        <div className="bg-zinc-900 border border-zinc-800 rounded p-2 overflow-x-auto" data-testid="ftmo-opt2d-heatmap">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="p-2 text-[10px] text-zinc-500 text-left">SL ↓ \ TP →</th>
                {grid.cols.map((tp) => (
                  <th key={tp} className="p-2 text-[10px] text-cyan-300 text-center font-mono">{tp}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((sl) => (
                <tr key={sl}>
                  <td className="p-2 text-[10px] text-cyan-300 font-mono font-semibold">{sl}</td>
                  {grid.cols.map((tp) => {
                    const cell = grid.map[sl]?.[tp];
                    const isOptimal = opt && opt.sl_pips === sl && opt.tp_pips === tp;
                    const colors = getCellColor(cell);
                    return (
                      <td
                        key={tp}
                        className="p-1 text-center"
                        style={{ backgroundColor: colors.bg }}
                        data-testid={`ftmo-opt2d-cell-${sl}-${tp}`}
                      >
                        {cell?.skipped ? (
                          <span className="text-[10px] text-zinc-600 italic">—</span>
                        ) : (
                          <div className={`flex flex-col items-center ${isOptimal ? 'ring-2 ring-cyan-400 ring-inset rounded' : ''}`} style={{ color: colors.fg }}>
                            <span className="font-mono font-semibold text-[11px]">
                              {metric === 'compliance' ? `${cell?.mc_compliance_pass_rate}%` : `${cell?.return_pct?.toFixed(1)}%`}
                              {isOptimal && <span className="ml-1">★</span>}
                            </span>
                            <span className="text-[9px] opacity-70 font-mono">
                              {metric === 'compliance' ? `r ${cell?.return_pct?.toFixed(0)}%` : `c ${cell?.mc_compliance_pass_rate}%`}
                            </span>
                            <span className="text-[8px] opacity-50 font-mono">
                              dd {cell?.mc_max_dd_p95}%
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-zinc-500 mt-2 px-1">
            Cada celda: {metric === 'compliance' ? 'Compliance % (grande)' : 'Return % (grande)'}, {metric === 'compliance' ? 'Return % (pequeño)' : 'Compliance % (pequeño)'}, MC MaxDD P95.
            Verde = bueno · Amarillo = medio · Rojo = malo · Gris = SKIP (R:R inválido).
            ★ marca la celda óptima.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default function BacktestLabFTMO() {
  const botId = 'ftmo';
  const [params, setParams] = useState(DEFAULTS_FTMO);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  // Auto-optimizer
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [optimizeError, setOptimizeError] = useState(null);
  const [optimizeCfg, setOptimizeCfg] = useState({
    risk_pct_min: 0.003,
    risk_pct_max: 0.012,
    risk_pct_step: 0.001,
    monte_carlo_n_sims: 500,
    monte_carlo_mode: 'shuffle',
    target_compliance_pct: 99,
  });
  // Auto-optimizer 2D (SL x TP)
  const [optimizing2D, setOptimizing2D] = useState(false);
  const [optimize2DResult, setOptimize2DResult] = useState(null);
  const [optimize2DError, setOptimize2DError] = useState(null);
  const [optimize2DCfg, setOptimize2DCfg] = useState({
    sl_pips_min: 6,
    sl_pips_max: 14,
    sl_pips_step: 2,
    tp_pips_min: 18,
    tp_pips_max: 36,
    tp_pips_step: 6,
    risk_pct: 0.005,
    monte_carlo_n_sims: 300,
    monte_carlo_mode: 'shuffle',
    target_compliance_pct: 95,
  });
  const [optimize2DMetric, setOptimize2DMetric] = useState('compliance'); // 'compliance' | 'return'

  const fetchHistory = async () => {
    try {
      const r = await fetch(`${API_URL}/api/ops/backtest?bot_id=${botId}&limit=20`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setHistory(data.items || []);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchHistory(); }, []);

  const updateParam = (key, val) => setParams((p) => ({ ...p, [key]: val }));

  const runBacktest = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      // Sanitizamos period_days null
      const payload = { ...params };
      if (payload.period_days === '' || payload.period_days === 0) payload.period_days = null;
      const r = await fetch(`${API_URL}/api/ops/backtest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ bot_id: botId, params: payload }),
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`HTTP ${r.status}: ${detail.slice(0, 300)}`);
      }
      const data = await r.json();
      setResult(data);
      setSelectedHistoryId(data.run_id);
      fetchHistory();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const runOptimize = async () => {
    setOptimizing(true);
    setOptimizeError(null);
    setOptimizeResult(null);
    try {
      const base_params = { ...params };
      if (base_params.period_days === '' || base_params.period_days === 0) base_params.period_days = null;
      const payload = {
        base_params,
        ...optimizeCfg,
      };
      const r = await fetch(`${API_URL}/api/ops/backtest/optimize_ftmo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`HTTP ${r.status}: ${detail.slice(0, 300)}`);
      }
      const data = await r.json();
      setOptimizeResult(data);
    } catch (e) {
      setOptimizeError(e.message);
    } finally {
      setOptimizing(false);
    }
  };

  const applyOptimal = () => {
    if (!optimizeResult?.optimal) return;
    const opt = optimizeResult.optimal;
    setParams((p) => ({
      ...p,
      risk_pct: opt.risk_pct,
      monte_carlo_n_sims: optimizeCfg.monte_carlo_n_sims,
      monte_carlo_mode: optimizeCfg.monte_carlo_mode,
      note: `Auto-optim: risk_pct=${(opt.risk_pct * 100).toFixed(2)}% (compliance=${opt.mc_compliance_pass_rate}%)`,
    }));
  };

  const updateOptimizeCfg = (k, v) => setOptimizeCfg((c) => ({ ...c, [k]: v }));

  const runOptimize2D = async () => {
    setOptimizing2D(true);
    setOptimize2DError(null);
    setOptimize2DResult(null);
    try {
      const base_params = { ...params };
      if (base_params.period_days === '' || base_params.period_days === 0) base_params.period_days = null;
      const payload = { base_params, ...optimize2DCfg };
      const r = await fetch(`${API_URL}/api/ops/backtest/optimize_ftmo_2d`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`HTTP ${r.status}: ${detail.slice(0, 300)}`);
      }
      const data = await r.json();
      setOptimize2DResult(data);
    } catch (e) {
      setOptimize2DError(e.message);
    } finally {
      setOptimizing2D(false);
    }
  };

  const applyOptimal2D = () => {
    if (!optimize2DResult?.optimal) return;
    const opt = optimize2DResult.optimal;
    setParams((p) => ({
      ...p,
      sl_pips: opt.sl_pips,
      tp_pips: opt.tp_pips,
      risk_pct: optimize2DCfg.risk_pct,
      note: `Auto-optim 2D: SL=${opt.sl_pips}/TP=${opt.tp_pips} (R:R 1:${opt.rr_ratio}) compliance=${opt.mc_compliance_pass_rate}%`,
    }));
  };

  const updateOptimize2DCfg = (k, v) => setOptimize2DCfg((c) => ({ ...c, [k]: v }));

  const loadFromHistory = async (runId) => {
    try {
      const r = await fetch(`${API_URL}/api/ops/backtest/${runId}`, { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setResult(data);
        setSelectedHistoryId(runId);
      }
    } catch (e) { /* silent */ }
  };

  const deleteRun = async (runId, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminar este run del historial?')) return;
    try {
      await fetch(`${API_URL}/api/ops/backtest/${runId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      fetchHistory();
      if (selectedHistoryId === runId) {
        setResult(null);
        setSelectedHistoryId(null);
      }
    } catch (e) { /* silent */ }
  };

  const applyPreset = (preset) => setParams({ ...DEFAULTS_FTMO, ...preset });

  const toggleCompare = (runId, e) => {
    e.stopPropagation();
    setCompareIds((ids) => {
      if (ids.includes(runId)) return ids.filter((x) => x !== runId);
      if (ids.length >= 3) return ids;
      return [...ids, runId];
    });
  };

  const openCompareModal = async () => {
    if (compareIds.length < 2) return;
    try {
      const runs = await Promise.all(
        compareIds.map(async (id) => {
          const r = await fetch(`${API_URL}/api/ops/backtest/${id}`, { headers: authHeaders() });
          return r.ok ? r.json() : null;
        })
      );
      setCompareData(runs.filter(Boolean));
      setShowCompare(true);
    } catch (e) { /* silent */ }
  };

  const closeCompareModal = () => { setShowCompare(false); setCompareData(null); };
  const clearCompare = () => { setCompareIds([]); closeCompareModal(); };

  const equityData = useMemo(() => {
    if (!result?.daily_equity) return [];
    return result.daily_equity.map((p) => ({
      ts: p.ts,
      label: new Date(p.ts).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      equity: parseFloat((p.equity || 0).toFixed(2)),
    }));
  }, [result]);

  const initialBalance = result?.aggregate?.initial_balance || params.initial_balance || 10000;
  const compliance = result?.ftmo_compliance;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* ============== LEFT: FORM ============== */}
      <Card className="lg:col-span-1 bg-zinc-950 border-zinc-800" data-testid="ftmo-backtest-form-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="w-4 h-4 text-amber-400" />
            FTMO XAUUSD — Parametros LBFP
          </CardTitle>
          <CardDescription className="text-xs">
            London Breakout Fixed Pips. {params.engine_mode === 'm1'
              ? 'Motor M1 ALTA FIDELIDAD: ventana OCO real + camino intra-vela (fiel al bot vivo).'
              : 'Motor H1 (rápido). Para validar la estrategia REAL usa el motor M1.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Selector de motor de backtest */}
          <div className="flex items-center gap-2 rounded border border-zinc-800 bg-zinc-900/50 p-2"
               data-testid="ftmo-engine-selector">
            <span className="text-xs text-zinc-400">Motor:</span>
            {[
              { v: 'h1', label: 'H1 (rápido)' },
              { v: 'm1', label: 'M1 (alta fidelidad)' },
            ].map((e) => (
              <Button key={e.v} size="sm" variant="outline"
                onClick={() => updateParam('engine_mode', e.v)}
                className={`h-7 px-3 text-xs ${
                  params.engine_mode === e.v
                    ? 'bg-cyan-700/40 border-cyan-600 text-cyan-200'
                    : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'
                }`}
                data-testid={`ftmo-engine-${e.v}`}>
                {e.label}
              </Button>
            ))}
            <span className="text-[10px] text-zinc-500 ml-auto">
              {params.engine_mode === 'm1' ? 'Reproduce el bot en vivo (OCO 07-12, SL/TP minuto a minuto)' : 'Aproximación H1 (puede engañar con SL estrecho)'}
            </span>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-amber-700 hover:bg-amber-900/40"
              onClick={() => applyPreset(PRESET_LIVE)} data-testid="ftmo-preset-live">
              Preset Live ($10k)
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-emerald-700 hover:bg-emerald-900/40"
              onClick={() => applyPreset(PRESET_LONDON_NY)} data-testid="ftmo-preset-london-ny">
              London + NY (B)
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-zinc-700 hover:bg-zinc-800"
              onClick={() => applyPreset(PRESET_AGGRESSIVE)} data-testid="ftmo-preset-aggressive">
              Agresivo 1.2%
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-zinc-700 hover:bg-zinc-800"
              onClick={() => applyPreset(PRESET_LONDON_FULL)} data-testid="ftmo-preset-london-full">
              London 08-09
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-zinc-700 hover:bg-zinc-800"
              onClick={() => applyPreset(PRESET_SWEEP_SPREAD3)} data-testid="ftmo-preset-spread3">
              Spread 3 pips
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-zinc-700 hover:bg-zinc-800"
              onClick={() => applyPreset(PRESET_SWEEP_SPREAD7)} data-testid="ftmo-preset-spread7">
              Spread 7 pips
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-violet-700 hover:bg-violet-900/40"
              onClick={() => applyPreset(PRESET_3Y)} data-testid="ftmo-preset-3y">
              3 anios + MC
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-violet-700 hover:bg-violet-900/40"
              onClick={() => applyPreset(PRESET_MC_BOOTSTRAP)} data-testid="ftmo-preset-mc-bootstrap">
              MC Bootstrap
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-cyan-700 hover:bg-cyan-900/40 text-cyan-200"
              onClick={() => applyPreset(PRESET_ATR_C3)} data-testid="ftmo-preset-atr-c3">
              ATR c3 (live)
            </Button>
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-cyan-500 bg-cyan-950/30 hover:bg-cyan-900/50 text-cyan-100 font-semibold"
              onClick={() => applyPreset(PRESET_M1_C3B)} data-testid="ftmo-preset-m1-c3b">
              ★ M1 c3b (real)
            </Button>
          </div>

          {/* Balance / Risk */}
          <div className="grid grid-cols-2 gap-2">
            <NumberField label="Balance inicial USD" value={params.initial_balance}
              onChange={(v) => updateParam('initial_balance', v)} step={1000} />
            <NumberField label="Risk %" value={(params.risk_pct * 100).toFixed(2)}
              onChange={(v) => updateParam('risk_pct', v / 100)} step={0.05}
              hint={`($${(params.initial_balance * params.risk_pct).toFixed(2)} por trade)`} />
          </div>

          {/* SL / TP / Spread */}
          <div className="grid grid-cols-3 gap-2">
            <NumberField label="SL pips" value={params.sl_pips}
              onChange={(v) => updateParam('sl_pips', v)} step={1} />
            <NumberField label="TP pips" value={params.tp_pips}
              onChange={(v) => updateParam('tp_pips', v)} step={1}
              hint={`R:R 1:${(params.tp_pips / params.sl_pips || 0).toFixed(2)}`} />
            <NumberField label="Spread pips" value={params.spread_pips}
              onChange={(v) => updateParam('spread_pips', v)} step={0.5} />
          </div>

          {/* Realismo de ejecucion + SL dinamico (FIX auditoria jun-2026) */}
          <div className="pt-2 border-t border-zinc-800" data-testid="ftmo-bt-realism-section">
            <p className="text-xs text-zinc-400 mb-2">SL dinámico y realismo</p>
            <div className="grid grid-cols-2 gap-2">
              {params.engine_mode === 'h1' && (
                <NumberField label="Slippage pips" value={params.slippage_pips}
                  onChange={(v) => updateParam('slippage_pips', v)} step={0.5}
                  hint="desliz. extra al spread en el fill" testid="ftmo-bt-slippage" />
              )}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-zinc-400">Modo SL</label>
                <div className="flex gap-1">
                  {['fixed', 'atr'].map((m) => (
                    <Button key={m} size="sm" variant="outline"
                      onClick={() => updateParam('sl_mode', m)}
                      className={`h-8 px-3 text-xs ${
                        params.sl_mode === m
                          ? 'bg-cyan-700/40 border-cyan-600 text-cyan-200'
                          : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'
                      }`}
                      data-testid={`ftmo-bt-slmode-${m}`}>
                      {m === 'fixed' ? 'Fijo' : 'ATR (volatilidad)'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            {params.engine_mode === 'h1' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Toggle label="Evaluar vela de entrada (capta stop-outs inmediatos)"
                  value={params.include_entry_bar}
                  onChange={(v) => updateParam('include_entry_bar', v)}
                  testid="ftmo-bt-include-entry-bar" />
                <Toggle label="Modelar gap de apertura (solo M1)"
                  value={params.model_gap_through}
                  onChange={(v) => updateParam('model_gap_through', v)}
                  testid="ftmo-bt-gap-through" />
              </div>
            )}
            {params.sl_mode === 'atr' && (
              <div className="grid grid-cols-3 gap-2 mt-2 bg-cyan-950/20 border border-cyan-900/40 rounded p-2">
                <NumberField label="ATR period" value={params.atr_period}
                  onChange={(v) => updateParam('atr_period', parseInt(v) || 14)} step={1}
                  testid="ftmo-bt-atr-period" />
                <NumberField label="ATR mult" value={params.atr_mult}
                  onChange={(v) => updateParam('atr_mult', v)} step={0.1}
                  hint="SL = ATR × mult" testid="ftmo-bt-atr-mult" />
                <NumberField label="R:R (TP)" value={params.rr}
                  onChange={(v) => updateParam('rr', v)} step={0.5}
                  hint="0 = usar TP pips" testid="ftmo-bt-rr" />
              </div>
            )}
          </div>

          {/* Alta fidelidad M1: ventana OCO + filtros de mejora */}
          {params.engine_mode === 'm1' && (
            <div className="pt-2 border-t border-cyan-900/50 bg-cyan-950/10 rounded p-2 space-y-2"
                 data-testid="ftmo-bt-m1-section">
              <p className="text-xs text-cyan-300 font-semibold">Alta fidelidad M1 (OCO real)</p>
              <div className="grid grid-cols-2 gap-2">
                <NumberField label="OCO arma (UTC)" value={params.entry_start_utc}
                  onChange={(v) => updateParam('entry_start_utc', parseInt(v) || 7)} step={1}
                  hint="live = 07" testid="ftmo-bt-m1-entry-start" />
                <NumberField label="OCO caduca (UTC)" value={params.entry_end_utc}
                  onChange={(v) => updateParam('entry_end_utc', parseInt(v) || 12)} step={1}
                  hint="excl. · live = 12" testid="ftmo-bt-m1-entry-end" />
              </div>
              {/* Filtro de tendencia HTF */}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-zinc-400">Filtro tendencia HTF</span>
                <Toggle label="Activar" value={params.htf_filter}
                  onChange={(v) => updateParam('htf_filter', v)} testid="ftmo-bt-m1-htf" />
              </div>
              {params.htf_filter && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-zinc-400">TF</label>
                    <div className="flex gap-1">
                      {['d1', 'h4'].map((tf) => (
                        <Button key={tf} size="sm" variant="outline"
                          onClick={() => updateParam('htf_tf', tf)}
                          className={`h-8 px-3 text-xs ${
                            params.htf_tf === tf
                              ? 'bg-cyan-700/40 border-cyan-600 text-cyan-200'
                              : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'
                          }`}
                          data-testid={`ftmo-bt-m1-htf-tf-${tf}`}>
                          {tf.toUpperCase()}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <NumberField label="EMA periodo" value={params.htf_ema_period}
                    onChange={(v) => updateParam('htf_ema_period', parseInt(v) || 20)} step={1}
                    testid="ftmo-bt-m1-htf-ema" />
                </div>
              )}
              {/* Filtro de rango asiatico + buffer */}
              <div className="grid grid-cols-3 gap-2">
                <NumberField label="Rango min (p)" value={params.min_range_pips}
                  onChange={(v) => updateParam('min_range_pips', v)} step={10}
                  hint="0 = off" testid="ftmo-bt-m1-range-min" />
                <NumberField label="Rango max (p)" value={params.max_range_pips}
                  onChange={(v) => updateParam('max_range_pips', v)} step={50}
                  testid="ftmo-bt-m1-range-max" />
                <NumberField label="Buffer (p)" value={params.entry_buffer_pips}
                  onChange={(v) => updateParam('entry_buffer_pips', v)} step={1}
                  hint="entrada más allá" testid="ftmo-bt-m1-buffer" />
              </div>
              {/* Gestion de salida */}
              <div className="grid grid-cols-3 gap-2">
                <NumberField label="BE @ R" value={params.be_at_r}
                  onChange={(v) => updateParam('be_at_r', v)} step={0.25}
                  hint="0 = off" testid="ftmo-bt-m1-be" />
                <NumberField label="Trail inicio R" value={params.trail_start_r}
                  onChange={(v) => updateParam('trail_start_r', v)} step={0.25}
                  hint="0 = off" testid="ftmo-bt-m1-trail-start" />
                <NumberField label="Parcial @ R" value={params.partial_tp_r}
                  onChange={(v) => updateParam('partial_tp_r', v)} step={0.25}
                  hint="0 = off" testid="ftmo-bt-m1-partial" />
              </div>
              <p className="text-[10px] text-zinc-500">
                Nota (backtest jun-2026): en esta data, BE/trailing/parciales RECORTAN los ganadores
                y bajan el PF. El edge está en dejar correr a un RR fijo (c3b RR2.5). Úsalos con cautela.
              </p>
            </div>
          )}

          {/* London window */}
          <div className="pt-2 border-t border-zinc-800">
            <p className="text-xs text-zinc-400 mb-2">Ventana London (UTC)</p>
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="London start" value={params.london_start_utc}
                onChange={(v) => updateParam('london_start_utc', parseInt(v) || 0)} step={1}
                hint="hora UTC" />
              <NumberField label="London end" value={params.london_end_utc}
                onChange={(v) => updateParam('london_end_utc', parseInt(v) || 0)} step={1}
                hint="inclusive (8=solo 08:00)" />
            </div>
          </div>

          {/* NY breakout (Opcion B) */}
          <div className="pt-2 border-t border-zinc-800">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-zinc-400">NY Breakout — Opción B</p>
              <Toggle label="Activar"
                value={params.enable_ny_breakout}
                onChange={(v) => updateParam('enable_ny_breakout', v)}
                testid="ftmo-toggle-ny" />
            </div>
            {params.enable_ny_breakout && (
              <div className="grid grid-cols-3 gap-2 bg-emerald-950/20 border border-emerald-900/40 rounded p-2">
                <NumberField label="Rango start" value={params.ny_range_start_utc}
                  onChange={(v) => updateParam('ny_range_start_utc', parseInt(v) || 0)} step={1}
                  hint="London open" />
                <NumberField label="Rango end" value={params.ny_range_end_utc}
                  onChange={(v) => updateParam('ny_range_end_utc', parseInt(v) || 0)} step={1}
                  hint="excl. (13=hasta 12)" />
                <NumberField label="NY hour" value={params.ny_hour_utc}
                  onChange={(v) => updateParam('ny_hour_utc', parseInt(v) || 0)} step={1}
                  hint="bar entrada NY" />
              </div>
            )}
          </div>

          {/* Trailing */}
          <div className="pt-2 border-t border-zinc-800">
            <p className="text-xs text-zinc-400 mb-2">Trailing multi-fase</p>
            <div className="grid grid-cols-3 gap-2">
              <NumberField label="Fase1 → BE %" value={(params.trail1_pct * 100).toFixed(2)}
                onChange={(v) => updateParam('trail1_pct', v / 100)} step={0.1} />
              <NumberField label="Fase2 % trigger" value={(params.trail2_pct * 100).toFixed(2)}
                onChange={(v) => updateParam('trail2_pct', v / 100)} step={0.1} />
              <NumberField label="Fase2 lock %" value={(params.trail2_lock_pct * 100).toFixed(2)}
                onChange={(v) => updateParam('trail2_lock_pct', v / 100)} step={0.1} />
            </div>
          </div>

          {/* Limits */}
          <div className="pt-2 border-t border-zinc-800">
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="Max trades/dia" value={params.max_trades_per_day}
                onChange={(v) => updateParam('max_trades_per_day', parseInt(v) || 1)} step={1} />
              <NumberField label="Lot cap" value={params.lot_cap}
                onChange={(v) => updateParam('lot_cap', v)} step={0.5} />
            </div>
            <div className="mt-2">
              <Toggle label="Stop on Win (no opera mas si gana 1er trade)" value={params.stop_on_win}
                onChange={(v) => updateParam('stop_on_win', v)} />
            </div>
          </div>

          {/* Advanced */}
          <div className="pt-2 border-t border-zinc-800">
            <p className="text-xs text-zinc-400 mb-2">Avanzado</p>
            <div className="mb-2">
              <p className="text-[10px] text-zinc-500 mb-1">Periodo rapido</p>
              <div className="flex flex-wrap gap-1">
                {[
                  { label: '3m', days: 90 },
                  { label: '6m', days: 180 },
                  { label: '1a', days: 365 },
                  { label: '2a', days: 730 },
                  { label: '3a', days: 1095 },
                  { label: '5a', days: 1825 },
                  { label: 'Todo', days: null },
                ].map((p) => {
                  const active = (params.period_days || null) === p.days;
                  return (
                    <Button
                      key={p.label}
                      size="sm"
                      variant="outline"
                      onClick={() => updateParam('period_days', p.days)}
                      className={`h-6 px-2 text-[11px] ${
                        active
                          ? 'bg-amber-700/40 border-amber-600 text-amber-200'
                          : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'
                      }`}
                      data-testid={`ftmo-period-${p.label.toLowerCase()}`}
                    >
                      {p.label}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="Commission USD/lot" value={params.commission_per_lot}
                onChange={(v) => updateParam('commission_per_lot', v)} step={0.5} />
              <NumberField label="Periodo dias" value={params.period_days || ''}
                onChange={(v) => updateParam('period_days', v && v > 0 ? parseInt(v) : null)} step={30}
                hint="vacio = todo · 1095=3a · 1825=5a" />
            </div>
          </div>

          {/* Monte Carlo */}
          <div className="pt-2 border-t border-zinc-800" data-testid="ftmo-bt-mc-section">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-zinc-400">Monte Carlo</p>
              <Toggle
                label={params.monte_carlo_n_sims > 0 ? `Activo (${params.monte_carlo_n_sims})` : 'Desactivado'}
                value={params.monte_carlo_n_sims > 0}
                onChange={(v) => updateParam('monte_carlo_n_sims', v ? 1000 : 0)}
                testid="ftmo-toggle-mc"
              />
            </div>
            {params.monte_carlo_n_sims > 0 && (
              <div className="space-y-2 bg-violet-950/20 border border-violet-900/40 rounded p-2">
                <div className="grid grid-cols-2 gap-2">
                  <NumberField label="N simulaciones" value={params.monte_carlo_n_sims}
                    onChange={(v) => updateParam('monte_carlo_n_sims', Math.max(0, parseInt(v) || 0))}
                    step={500} hint="1000-2000 tipico" testid="ftmo-mc-n-sims" />
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-zinc-400">Modo</label>
                    <select
                      value={params.monte_carlo_mode}
                      onChange={(e) => updateParam('monte_carlo_mode', e.target.value)}
                      className="h-8 text-sm bg-zinc-900 border border-zinc-700 rounded px-2"
                      data-testid="ftmo-mc-mode"
                    >
                      <option value="shuffle">shuffle (orden FTMO)</option>
                      <option value="bootstrap">bootstrap (proyeccion)</option>
                    </select>
                    <span className="text-[10px] text-zinc-500">
                      {params.monte_carlo_mode === 'shuffle'
                        ? 'Reordena trades reales · mide sensibilidad al orden'
                        : 'Resampleo con reemplazo · proyecta forward'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <NumberField label="Daily DD %" value={params.monte_carlo_daily_dd_limit_pct}
                    onChange={(v) => updateParam('monte_carlo_daily_dd_limit_pct', v)} step={0.5}
                    hint="FTMO=5" testid="ftmo-mc-daily" />
                  <NumberField label="Total DD %" value={params.monte_carlo_total_dd_limit_pct}
                    onChange={(v) => updateParam('monte_carlo_total_dd_limit_pct', v)} step={0.5}
                    hint="FTMO=10" testid="ftmo-mc-total" />
                  <NumberField label="Target %" value={params.monte_carlo_target_pct}
                    onChange={(v) => updateParam('monte_carlo_target_pct', v)} step={0.5}
                    hint="Step1=10" testid="ftmo-mc-target" />
                </div>
                <NumberField label="Seed (reproducibilidad)" value={params.monte_carlo_seed}
                  onChange={(v) => updateParam('monte_carlo_seed', Math.max(0, parseInt(v) || 0))}
                  step={1} testid="ftmo-mc-seed" />
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Nota</label>
            <Input
              type="text"
              value={params.note || ''}
              onChange={(e) => updateParam('note', e.target.value)}
              placeholder="ej: SL tight + spread broker A"
              className="h-8 text-sm bg-zinc-900 border-zinc-700"
              data-testid="ftmo-bt-note"
            />
          </div>

          <Button
            onClick={runBacktest}
            disabled={running}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white"
            data-testid="ftmo-run-backtest-btn"
          >
            {running ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin">⏳</span>
                Backtesting XAUUSD...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Play className="w-4 h-4" /> Run FTMO Backtest
              </span>
            )}
          </Button>

          {/* Auto-optimizer */}
          <div className="pt-2 border-t border-violet-900/40">
            <p className="text-[10px] text-violet-300 uppercase tracking-wider mb-2 font-semibold">
              Auto-optimizar FTMO
            </p>
            <p className="text-[10px] text-zinc-500 mb-2">
              Sweep de risk% buscando el maximo return con Compliance MC &gt;= target.
            </p>
            <div className="grid grid-cols-3 gap-1 mb-2">
              <NumberField label="Risk min%" value={(optimizeCfg.risk_pct_min * 100).toFixed(2)}
                onChange={(v) => updateOptimizeCfg('risk_pct_min', v / 100)} step={0.05}
                testid="ftmo-opt-risk-min" />
              <NumberField label="Risk max%" value={(optimizeCfg.risk_pct_max * 100).toFixed(2)}
                onChange={(v) => updateOptimizeCfg('risk_pct_max', v / 100)} step={0.05}
                testid="ftmo-opt-risk-max" />
              <NumberField label="Step%" value={(optimizeCfg.risk_pct_step * 100).toFixed(2)}
                onChange={(v) => updateOptimizeCfg('risk_pct_step', v / 100)} step={0.05}
                testid="ftmo-opt-risk-step" />
            </div>
            <div className="grid grid-cols-2 gap-1 mb-2">
              <NumberField label="MC sims/iter" value={optimizeCfg.monte_carlo_n_sims}
                onChange={(v) => updateOptimizeCfg('monte_carlo_n_sims', Math.max(100, parseInt(v) || 100))}
                step={100} testid="ftmo-opt-mc-sims" />
              <NumberField label="Target compl%" value={optimizeCfg.target_compliance_pct}
                onChange={(v) => updateOptimizeCfg('target_compliance_pct', v)} step={1}
                hint="99 = ultra-conservador" testid="ftmo-opt-target" />
            </div>
            <Button
              onClick={runOptimize}
              disabled={optimizing || running}
              className="w-full bg-violet-700 hover:bg-violet-600 text-white"
              data-testid="ftmo-run-optimize-btn"
            >
              {optimizing ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Optimizando ({Math.ceil((optimizeCfg.risk_pct_max - optimizeCfg.risk_pct_min) / optimizeCfg.risk_pct_step) + 1} iters)...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Auto-optimizar FTMO
                </span>
              )}
            </Button>
            {optimizeError && (
              <div className="text-xs text-red-400 bg-red-950/30 border border-red-900 rounded p-2 mt-2 flex items-start gap-2" data-testid="ftmo-opt-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="break-words">{optimizeError}</span>
              </div>
            )}
          </div>

          {/* Auto-optimizer 2D (SL x TP) */}
          <div className="pt-2 border-t border-cyan-900/40">
            <p className="text-[10px] text-cyan-300 uppercase tracking-wider mb-2 font-semibold">
              Optimizer 2D — SL × TP heatmap
            </p>
            <p className="text-[10px] text-zinc-500 mb-2">
              Mapa de calor: barre combos SL/TP con risk fijo. Encuentra el R:R optimo.
            </p>
            <div className="grid grid-cols-3 gap-1 mb-2">
              <NumberField label="SL min" value={optimize2DCfg.sl_pips_min}
                onChange={(v) => updateOptimize2DCfg('sl_pips_min', v)} step={1}
                testid="ftmo-opt2d-sl-min" />
              <NumberField label="SL max" value={optimize2DCfg.sl_pips_max}
                onChange={(v) => updateOptimize2DCfg('sl_pips_max', v)} step={1}
                testid="ftmo-opt2d-sl-max" />
              <NumberField label="SL step" value={optimize2DCfg.sl_pips_step}
                onChange={(v) => updateOptimize2DCfg('sl_pips_step', v)} step={1}
                testid="ftmo-opt2d-sl-step" />
            </div>
            <div className="grid grid-cols-3 gap-1 mb-2">
              <NumberField label="TP min" value={optimize2DCfg.tp_pips_min}
                onChange={(v) => updateOptimize2DCfg('tp_pips_min', v)} step={2}
                testid="ftmo-opt2d-tp-min" />
              <NumberField label="TP max" value={optimize2DCfg.tp_pips_max}
                onChange={(v) => updateOptimize2DCfg('tp_pips_max', v)} step={2}
                testid="ftmo-opt2d-tp-max" />
              <NumberField label="TP step" value={optimize2DCfg.tp_pips_step}
                onChange={(v) => updateOptimize2DCfg('tp_pips_step', v)} step={1}
                testid="ftmo-opt2d-tp-step" />
            </div>
            <div className="grid grid-cols-3 gap-1 mb-2">
              <NumberField label="Risk %" value={(optimize2DCfg.risk_pct * 100).toFixed(2)}
                onChange={(v) => updateOptimize2DCfg('risk_pct', v / 100)} step={0.05}
                hint="fijo en sweep" testid="ftmo-opt2d-risk" />
              <NumberField label="MC sims" value={optimize2DCfg.monte_carlo_n_sims}
                onChange={(v) => updateOptimize2DCfg('monte_carlo_n_sims', Math.max(100, parseInt(v) || 100))}
                step={100} testid="ftmo-opt2d-mc" />
              <NumberField label="Target%" value={optimize2DCfg.target_compliance_pct}
                onChange={(v) => updateOptimize2DCfg('target_compliance_pct', v)} step={1}
                testid="ftmo-opt2d-target" />
            </div>
            <Button
              onClick={runOptimize2D}
              disabled={optimizing2D || optimizing || running}
              className="w-full bg-cyan-700 hover:bg-cyan-600 text-white"
              data-testid="ftmo-run-optimize2d-btn"
            >
              {optimizing2D ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Optimizando 2D ({Math.max(1, Math.ceil((optimize2DCfg.sl_pips_max - optimize2DCfg.sl_pips_min) / optimize2DCfg.sl_pips_step) + 1) * Math.max(1, Math.ceil((optimize2DCfg.tp_pips_max - optimize2DCfg.tp_pips_min) / optimize2DCfg.tp_pips_step) + 1)} celdas)...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Grid3X3 className="w-4 h-4" /> Auto-optimizar SL/TP
                </span>
              )}
            </Button>
            {optimize2DError && (
              <div className="text-xs text-red-400 bg-red-950/30 border border-red-900 rounded p-2 mt-2 flex items-start gap-2" data-testid="ftmo-opt2d-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="break-words">{optimize2DError}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="text-xs text-red-400 bg-red-950/30 border border-red-900 rounded p-2 flex items-start gap-2" data-testid="ftmo-bt-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="break-words">{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============== CENTER: RESULTS ============== */}
      <Card className="lg:col-span-2 bg-zinc-950 border-zinc-800" data-testid="ftmo-backtest-results-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              Resultados — XAUUSD LBFP
            </span>
            {result && (
              <Badge variant="outline" className="font-mono text-xs">
                run_id: {result.run_id}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!result ? (
            <div className="text-center py-16 text-zinc-500 text-sm">
              {running ? (
                <p>Corriendo backtest XAUUSD...</p>
              ) : (
                <p>Configura parametros a la izquierda y lanza Run.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* FTMO Compliance Banner */}
              {compliance && (
                <div className={`rounded p-2 border flex items-center justify-between ${
                  compliance.all_ok
                    ? 'bg-emerald-950/30 border-emerald-800'
                    : 'bg-red-950/30 border-red-800'
                }`} data-testid="ftmo-compliance-banner">
                  <div className="flex items-center gap-2">
                    {compliance.all_ok ? (
                      <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <ShieldAlert className="w-5 h-5 text-red-400" />
                    )}
                    <span className={`text-sm font-semibold ${compliance.all_ok ? 'text-emerald-300' : 'text-red-300'}`}>
                      FTMO Compliance: {compliance.all_ok ? 'PASS' : 'FAIL'}
                    </span>
                  </div>
                  <div className="flex gap-2 text-[10px]">
                    <Badge variant="outline" className={compliance.daily_dd_ok ? 'border-emerald-700 text-emerald-300' : 'border-red-700 text-red-300'}>
                      Daily DD {compliance.max_daily_dd_pct}% {compliance.daily_dd_ok ? '✓' : '✗ (>5%)'}
                    </Badge>
                    <Badge variant="outline" className={compliance.total_dd_ok ? 'border-emerald-700 text-emerald-300' : 'border-red-700 text-red-300'}>
                      Total DD {compliance.max_total_dd_pct}% {compliance.total_dd_ok ? '✓' : '✗ (>10%)'}
                    </Badge>
                    <Badge variant="outline" className={compliance.step1_target_hit ? 'border-emerald-700 text-emerald-300' : 'border-zinc-700 text-zinc-400'}>
                      Step1 10% {compliance.step1_target_hit ? '✓' : '○'}
                    </Badge>
                    <Badge variant="outline" className={compliance.step2_target_hit ? 'border-emerald-700 text-emerald-300' : 'border-zinc-700 text-zinc-400'}>
                      Step2 5% {compliance.step2_target_hit ? '✓' : '○'}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Reliability / honestidad del backtest (FIX auditoria jun-2026) */}
              {result.aggregate.reliability_level && (() => {
                const lvl = result.aggregate.reliability_level;
                const styles = {
                  baja: { box: 'bg-red-950/40 border-red-700', text: 'text-red-300', icon: <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" /> },
                  media: { box: 'bg-amber-950/40 border-amber-700', text: 'text-amber-300', icon: <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" /> },
                  ok: { box: 'bg-emerald-950/20 border-emerald-900', text: 'text-emerald-400', icon: <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> },
                }[lvl] || { box: 'bg-zinc-900 border-zinc-700', text: 'text-zinc-300', icon: <AlertCircle className="w-5 h-5 text-zinc-400 shrink-0" /> };
                const label = { baja: 'Fiabilidad BAJA', media: 'Fiabilidad MEDIA', ok: 'Fiabilidad OK' }[lvl] || 'Fiabilidad';
                return (
                  <div className={`rounded p-3 border flex items-start gap-2 ${styles.box}`} data-testid="backtest-reliability-banner">
                    {styles.icon}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-semibold ${styles.text}`}>{label}</span>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
                          ambig. H1 {result.aggregate.intrabar_ambiguous_pct}%
                        </Badge>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
                          SL {result.aggregate.sl_mode}{result.aggregate.slippage_pips != null ? ` · slip ${result.aggregate.slippage_pips}p` : ''}
                        </Badge>
                      </div>
                      <p className={`text-xs mt-1 ${lvl === 'ok' ? 'text-zinc-400' : styles.text}`} data-testid="backtest-reliability-msg">
                        {result.aggregate.reliability}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Aggregate KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Trades</p>
                  <p className="text-xl font-semibold text-zinc-100">{result.aggregate.total_trades}</p>
                </div>
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Win Rate</p>
                  <p className="text-xl font-semibold text-zinc-100">{result.aggregate.win_rate.toFixed(1)}%</p>
                </div>
                <div className={`rounded p-2 ${result.aggregate.net_pnl >= 0 ? 'bg-emerald-950/40' : 'bg-red-950/40'}`}>
                  <p className="text-[10px] text-zinc-500 uppercase">Net PnL</p>
                  <p className={`text-xl font-semibold ${result.aggregate.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.aggregate.net_pnl >= 0 ? '+' : ''}{formatUSD(result.aggregate.net_pnl)}
                  </p>
                </div>
                <div className={`rounded p-2 ${result.aggregate.return_pct >= 0 ? 'bg-emerald-950/40' : 'bg-red-950/40'}`}>
                  <p className="text-[10px] text-zinc-500 uppercase">Return</p>
                  <p className={`text-xl font-semibold ${result.aggregate.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.aggregate.return_pct >= 0 ? '+' : ''}{result.aggregate.return_pct.toFixed(2)}%
                  </p>
                </div>
              </div>

              {/* Secondary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">PF</p>
                  <p className="text-sm font-mono text-zinc-100">{result.aggregate.profit_factor ?? '∞'}</p>
                </div>
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Max DD</p>
                  <p className="text-sm font-mono text-red-400">{result.aggregate.max_dd_pct.toFixed(2)}%</p>
                </div>
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Avg Win</p>
                  <p className="text-sm font-mono text-emerald-400">${result.aggregate.avg_win}</p>
                </div>
                <div className="bg-zinc-900 rounded p-2">
                  <p className="text-[10px] text-zinc-500 uppercase">Avg Loss</p>
                  <p className="text-sm font-mono text-red-400">${result.aggregate.avg_loss}</p>
                </div>
              </div>

              {/* KPIs especificos del motor M1 (alta fidelidad) */}
              {result.aggregate.engine_mode === 'm1' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ftmo-m1-kpis">
                  <div className="bg-cyan-950/20 border border-cyan-900/40 rounded p-2">
                    <p className="text-[10px] text-zinc-500 uppercase">Stop &lt;=1min</p>
                    <p className={`text-sm font-mono ${result.aggregate.immediate_stop_pct > 20 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {result.aggregate.immediate_stop_pct}%
                    </p>
                  </div>
                  <div className="bg-cyan-950/20 border border-cyan-900/40 rounded p-2">
                    <p className="text-[10px] text-zinc-500 uppercase">Total R</p>
                    <p className={`text-sm font-mono ${result.aggregate.total_r >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {result.aggregate.total_r >= 0 ? '+' : ''}{result.aggregate.total_r}
                    </p>
                  </div>
                  <div className="bg-cyan-950/20 border border-cyan-900/40 rounded p-2">
                    <p className="text-[10px] text-zinc-500 uppercase">MaxDD (R)</p>
                    <p className="text-sm font-mono text-red-400">{result.aggregate.maxdd_r}</p>
                  </div>
                  <div className="bg-cyan-950/20 border border-cyan-900/40 rounded p-2">
                    <p className="text-[10px] text-zinc-500 uppercase">Conflictos / Timeouts</p>
                    <p className="text-sm font-mono text-zinc-300">{result.aggregate.intrabar_conflict} / {result.aggregate.timeouts}</p>
                  </div>
                </div>
              )}

              {/* Equity curve */}
              {equityData.length > 1 && (
                <div>
                  <p className="text-xs text-zinc-400 mb-1">Equity curve</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={equityData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} minTickGap={20} />
                      <YAxis tick={{ fontSize: 10, fill: '#71717a' }}
                             domain={['auto', 'auto']}
                             tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatUSD(v)} />
                      <ReferenceLine y={initialBalance} stroke="#52525b" strokeDasharray="3 3"
                        label={{ value: 'Inicial', fill: '#71717a', fontSize: 10 }} />
                      <Line type="monotone" dataKey="equity" stroke="#f59e0b" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Session breakdown (London vs NY) */}
              {result.session_breakdown && result.session_breakdown.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-400 mb-1">Session breakdown</p>
                  <div className="grid grid-cols-2 gap-2">
                    {result.session_breakdown.map((s) => (
                      <div key={s.session}
                        className={`rounded p-2 border ${
                          s.session === 'london'
                            ? 'bg-amber-950/30 border-amber-900'
                            : 'bg-emerald-950/30 border-emerald-900'
                        }`}
                        data-testid={`ftmo-session-${s.session}`}>
                        <p className="text-[10px] uppercase text-zinc-500">
                          {s.session === 'london' ? 'London 08:00' : 'NY 13:00'}
                        </p>
                        <div className="grid grid-cols-3 gap-1 mt-1">
                          <div>
                            <p className="text-[10px] text-zinc-500">trades</p>
                            <p className="text-sm font-mono">{s.trades}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-zinc-500">WR</p>
                            <p className="text-sm font-mono">{s.wr}%</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-zinc-500">PnL</p>
                            <p className={`text-sm font-mono ${s.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatUSD(s.pnl)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly breakdown */}
              {result.monthly_breakdown && result.monthly_breakdown.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-400 mb-1">Monthly breakdown</p>
                  <Table>
                    <TableHeader>
                      <TableRow className="text-[10px] uppercase">
                        <TableHead>Mes</TableHead>
                        <TableHead className="text-right">Trades</TableHead>
                        <TableHead className="text-right">WR%</TableHead>
                        <TableHead className="text-right">PnL</TableHead>
                        <TableHead className="text-right">Balance final</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.monthly_breakdown.map((m) => (
                        <TableRow key={m.month}>
                          <TableCell className="font-mono text-xs">{m.month}</TableCell>
                          <TableCell className="text-right text-xs">{m.trades}</TableCell>
                          <TableCell className="text-right text-xs">{m.wr}%</TableCell>
                          <TableCell className={`text-right text-xs font-mono ${m.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatUSD(m.pnl)}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">{formatUSD(m.balance_end)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <p className="text-[10px] text-zinc-500">
                Periodo: {result.aggregate.period_start} → {result.aggregate.period_end} ·
                Candles: {result.aggregate.candles_loaded} ·
                Duracion run: {result.duration_sec?.toFixed(1)}s
              </p>
              {result.note && <p className="text-xs italic text-zinc-400">Nota: {result.note}</p>}

              {/* Monte Carlo */}
              {result.monte_carlo && !result.monte_carlo.skipped && (
                <MonteCarloPanel mc={result.monte_carlo} />
              )}
              {result.monte_carlo?.skipped && (
                <div className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900 rounded p-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Monte Carlo no se ejecuto: {result.monte_carlo.reason || 'desconocido'}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============== AUTO-OPTIMIZE RESULTS ============== */}
      {(optimizing || optimizeResult) && (
        <OptimizeFTMOPanel
          loading={optimizing}
          result={optimizeResult}
          onApply={applyOptimal}
          targetCompliance={optimizeCfg.target_compliance_pct}
        />
      )}

      {/* ============== AUTO-OPTIMIZE 2D RESULTS ============== */}
      {(optimizing2D || optimize2DResult) && (
        <Optimize2DPanel
          loading={optimizing2D}
          result={optimize2DResult}
          onApply={applyOptimal2D}
          targetCompliance={optimize2DCfg.target_compliance_pct}
          metric={optimize2DMetric}
          onMetricChange={setOptimize2DMetric}
        />
      )}

      {/* ============== HISTORY ============== */}
      <Card className="lg:col-span-3 bg-zinc-950 border-zinc-800" data-testid="ftmo-backtest-history-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-zinc-400" />
              Historial FTMO ({history.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              {compareIds.length > 0 && (
                <Badge variant="outline" className="border-amber-700 text-amber-300 text-xs">
                  {compareIds.length}/3 seleccionados
                </Badge>
              )}
              <Button size="sm" variant="outline"
                className="h-7 text-xs border-amber-700 hover:bg-amber-900/40 disabled:opacity-30"
                disabled={compareIds.length < 2}
                onClick={openCompareModal}
                data-testid="ftmo-open-compare-btn">
                <GitCompare className="w-3 h-3 mr-1" />
                Comparar ({compareIds.length})
              </Button>
              {compareIds.length > 0 && (
                <Button size="sm" variant="ghost"
                  className="h-7 text-xs text-zinc-400"
                  onClick={clearCompare}
                  data-testid="ftmo-clear-compare-btn">
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-center text-zinc-500 text-sm py-4">Sin runs previos.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-[10px] uppercase">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Run ID</TableHead>
                  <TableHead>SL/TP</TableHead>
                  <TableHead>Spread</TableHead>
                  <TableHead>Risk%</TableHead>
                  <TableHead>London</TableHead>
                  <TableHead className="text-right">Trades</TableHead>
                  <TableHead className="text-right">WR%</TableHead>
                  <TableHead className="text-right">PF</TableHead>
                  <TableHead className="text-right">Return%</TableHead>
                  <TableHead className="text-right">MaxDD%</TableHead>
                  <TableHead>FTMO</TableHead>
                  <TableHead>Nota</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.run_id}
                    onClick={() => loadFromHistory(h.run_id)}
                    className={`cursor-pointer hover:bg-zinc-900 ${
                      selectedHistoryId === h.run_id ? 'bg-amber-950/30' : ''
                    }`}
                    data-testid={`ftmo-history-row-${h.run_id}`}>
                    <TableCell onClick={(e) => toggleCompare(h.run_id, e)}>
                      <input
                        type="checkbox"
                        checked={compareIds.includes(h.run_id)}
                        readOnly
                        className="accent-amber-500 cursor-pointer"
                        data-testid={`ftmo-compare-check-${h.run_id}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs">{formatTs(h.finished_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{h.run_id}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {h.params?.sl_pips}/{h.params?.tp_pips}
                    </TableCell>
                    <TableCell className="text-xs">{h.params?.spread_pips}p</TableCell>
                    <TableCell className="text-xs">{((h.params?.risk_pct || 0) * 100).toFixed(2)}%</TableCell>
                    <TableCell className="text-xs font-mono">
                      {h.params?.london_start_utc}-{h.params?.london_end_utc}
                    </TableCell>
                    <TableCell className="text-right text-xs">{h.aggregate?.total_trades || 0}</TableCell>
                    <TableCell className="text-right text-xs">{h.aggregate?.win_rate?.toFixed(1) || '0'}%</TableCell>
                    <TableCell className="text-right text-xs">{h.aggregate?.profit_factor ?? '—'}</TableCell>
                    <TableCell className={`text-right text-xs font-semibold ${(h.aggregate?.return_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(h.aggregate?.return_pct || 0).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right text-xs text-red-400">{(h.aggregate?.max_dd_pct || 0).toFixed(2)}%</TableCell>
                    <TableCell>
                      {h.ftmo_compliance?.all_ok ? (
                        <Badge variant="outline" className="border-emerald-700 text-emerald-300 text-[10px]">PASS</Badge>
                      ) : (
                        <Badge variant="outline" className="border-red-700 text-red-300 text-[10px]">FAIL</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400 max-w-[200px] truncate">
                      {h.note || '—'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-950/30"
                        onClick={(e) => deleteRun(h.run_id, e)}
                        data-testid={`ftmo-delete-run-${h.run_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ============== COMPARE MODAL ============== */}
      {showCompare && compareData && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeCompareModal}
          data-testid="ftmo-compare-modal"
        >
          <Card
            className="bg-zinc-950 border-zinc-700 max-w-7xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GitCompare className="w-5 h-5 text-amber-400" />
                  Comparativa FTMO ({compareData.length})
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={closeCompareModal} className="h-8 w-8 p-0"
                  data-testid="ftmo-close-compare-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription className="text-xs">
                Side-by-side. La columna verde es el mejor en cada metrica.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {(() => {
                const rets = compareData.map((c) => c.aggregate?.return_pct ?? -999);
                const wrs = compareData.map((c) => c.aggregate?.win_rate ?? 0);
                const pfs = compareData.map((c) => c.aggregate?.profit_factor ?? 0);
                const dds = compareData.map((c) => c.aggregate?.max_dd_pct ?? 999);
                const bestRet = rets.indexOf(Math.max(...rets));
                const bestWR = wrs.indexOf(Math.max(...wrs));
                const bestPF = pfs.indexOf(Math.max(...pfs));
                const bestDD = dds.indexOf(Math.min(...dds));

                return (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Metrica</TableHead>
                        {compareData.map((c) => (
                          <TableHead key={c.run_id} className="text-xs">
                            <div className="flex flex-col">
                              <span className="font-mono text-amber-300">{c.run_id}</span>
                              <span className="text-[10px] text-zinc-500">{formatTs(c.finished_at)}</span>
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">SL / TP (pips)</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs font-mono">
                            {c.params?.sl_pips} / {c.params?.tp_pips}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Spread</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{c.params?.spread_pips} pips</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Risk %</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{((c.params?.risk_pct || 0) * 100).toFixed(2)}%</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">London (UTC)</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">
                            {c.params?.london_start_utc}-{c.params?.london_end_utc}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Stop on Win</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{c.params?.stop_on_win ? '✓' : '✗'}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="border-t-2 border-zinc-700">
                        <TableCell className="text-xs font-semibold">Trades</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">{c.aggregate?.total_trades || 0}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Win Rate</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs ${i === bestWR ? 'bg-emerald-950/40 text-emerald-300 font-bold' : ''}`}>
                            {c.aggregate?.win_rate?.toFixed(2) || '0'}%
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Profit Factor</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs ${i === bestPF ? 'bg-emerald-950/40 text-emerald-300 font-bold' : ''}`}>
                            {c.aggregate?.profit_factor ?? '∞'}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Net PnL</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className={`text-xs font-mono ${(c.aggregate?.net_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatUSD(c.aggregate?.net_pnl || 0)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Return %</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs font-bold ${i === bestRet ? 'bg-emerald-950/40 text-emerald-300' : (c.aggregate?.return_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {(c.aggregate?.return_pct || 0).toFixed(2)}%
                            {i === bestRet && <span className="ml-1 text-[10px]">★</span>}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Max DD %</TableCell>
                        {compareData.map((c, i) => (
                          <TableCell key={c.run_id} className={`text-xs ${i === bestDD ? 'bg-emerald-950/40 text-emerald-300 font-bold' : 'text-red-400'}`}>
                            {(c.aggregate?.max_dd_pct || 0).toFixed(2)}%
                            {i === bestDD && <span className="ml-1 text-[10px]">★</span>}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">FTMO PASS</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs">
                            {c.ftmo_compliance?.all_ok ? (
                              <span className="text-emerald-400">✓ PASS</span>
                            ) : (
                              <span className="text-red-400">✗ FAIL</span>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-xs font-semibold">Nota</TableCell>
                        {compareData.map((c) => (
                          <TableCell key={c.run_id} className="text-xs text-zinc-400 italic">{c.note || '—'}</TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
